import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Theme } from "./theme";
import { darkTheme, lightTheme } from "./theme";
import { isThemeLoaded, loadTheme } from "./loadTheme";
import { saveDarkThemePreferenceToLocalStorage } from "./themePreferences";

export interface SiteThemeContextValue {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  toggleTheme: () => void;
}

const SiteThemeContext = createContext<SiteThemeContextValue | null>(null);

export function SiteThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDarkState] = useState(false);
  const [themeReady, setThemeReady] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    return isThemeLoaded();
  });
  const skipThemeEffect = useRef(true);

  useLayoutEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("[theme] SiteThemeProvider useLayoutEffect");
    }
    const preferred = loadTheme();
    setIsDarkState(preferred);
    setThemeReady(true);
    skipThemeEffect.current = false;
  }, []);

  useEffect(() => {
    if (skipThemeEffect.current) {
      return;
    }

    loadTheme(isDark);
  }, [isDark]);

  const setIsDark = useCallback((value: boolean) => {
    setIsDarkState(value);
    saveDarkThemePreferenceToLocalStorage(value);
    loadTheme(value);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkState((prev) => {
      const next = !prev;
      saveDarkThemePreferenceToLocalStorage(next);
      loadTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ isDark, setIsDark, toggleTheme }), [isDark, setIsDark, toggleTheme]);

  if (!themeReady) {
    return null;
  }

  return <SiteThemeContext.Provider value={value}>{children}</SiteThemeContext.Provider>;
}

export function useSiteTheme(): SiteThemeContextValue {
  const ctx = useContext(SiteThemeContext);
  if (ctx === null) {
    throw new Error("useSiteTheme must be used within SiteThemeProvider");
  }
  return ctx;
}
